import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, CheckCircle2, ChevronDown, RotateCcw } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import type { TicketDetail } from '@aurora/contracts';
import { cn } from '../../lib/cn';
import { useStatuses, type DirectoryStatus } from '../../lib/directory';
import { describeError } from '../../components/toast';
import { Dialog } from '../../components/dialog';
import { Badge, Button, Field, Spinner, Textarea } from '../../components/ui';
import { CustomFieldInputs, isUnanswered, type TicketFormField } from './custom-fields';
import { firstOfCategory, useTicketUpdate } from './detail-data';
import { ticketStatusTone } from './status-tone';

/**
 * Moving a ticket through its lifecycle: the status menu, Resolve and Reopen.
 *
 * Resolve used to be one click that failed in silence. When an administrator
 * had marked a field "needed before resolving", the spinner flashed, the
 * status stayed put, nothing was said — and nothing on the page could fill the
 * field in anyway. Resolving now opens a short dialog that asks for exactly
 * what the server will ask for: the fields still needed to close, and the
 * resolution note the requester is sent (required when the desk's setting says
 * so). Both go in one request, so a refusal leaves nothing half done.
 */

interface ResolveTarget {
  key: string;
  name: string;
  category: 'resolved' | 'closed';
}

export function useLifecycle(ticket: TicketDetail) {
  const statuses = useStatuses();
  const resolved = firstOfCategory(statuses.data, 'resolved');
  const open = firstOfCategory(statuses.data, 'open');
  return { statuses: statuses.data ?? [], resolved, open, loading: statuses.isLoading };
}

/** The status, as a menu of every other status the ticket can move to. */
export function StatusMenu({
  ticket,
  editable,
  onResolve,
}: {
  ticket: TicketDetail;
  editable: boolean;
  /** Called instead of changing straight away for a finishing status. */
  onResolve: (target: ResolveTarget) => void;
}) {
  const { statuses } = useLifecycle(ticket);
  const update = useTicketUpdate(String(ticket.number));
  const tone = ticketStatusTone(ticket.status.category);

  if (!editable || statuses.length === 0) {
    return (
      <Badge tone={tone} dot>
        {ticket.status.label}
      </Badge>
    );
  }

  const choose = (status: DirectoryStatus) => {
    if (status.key === ticket.status.key) return;
    if (status.category === 'resolved' || status.category === 'closed') {
      onResolve({ key: status.key, name: status.name, category: status.category });
      return;
    }
    update.mutate({
      body: { status: status.key },
      optimistic: { status: { ...ticket.status, key: status.key, label: status.name, category: status.category } },
      success: `Status set to ${status.name}`,
    });
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Status: ${ticket.status.label}. Change status`}
          className="inline-flex items-center gap-1 rounded-full outline-offset-2 disabled:opacity-60"
          disabled={update.isPending}
        >
          <Badge tone={tone} dot>
            {ticket.status.label}
            {update.isPending ? (
              <Spinner className="size-3" label="Saving" />
            ) : (
              <ChevronDown aria-hidden className="size-3 opacity-70" />
            )}
          </Badge>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="animate-pop z-50 min-w-48 rounded-card border border-line bg-raised p-1 shadow-float"
        >
          {statuses.map((status) => {
            const current = status.key === ticket.status.key;
            return (
              <DropdownMenu.Item
                key={status.key}
                onSelect={() => choose(status)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body outline-none data-highlighted:bg-sunken',
                  current ? 'text-ink-subtle' : 'text-ink',
                )}
              >
                <Check aria-hidden className={cn('size-3.5', current ? 'text-accent' : 'invisible')} />
                {status.name}
                {(status.category === 'resolved' || status.category === 'closed') && !current && (
                  <span className="ms-auto text-mini text-ink-subtle">asks for a note</span>
                )}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** The header's primary action: Resolve while open, Reopen once finished. */
export function LifecycleButton({
  ticket,
  onResolve,
}: {
  ticket: TicketDetail;
  onResolve: (target: ResolveTarget) => void;
}) {
  const { resolved, open } = useLifecycle(ticket);
  const update = useTicketUpdate(String(ticket.number));
  const finished = ticket.status.category === 'resolved' || ticket.status.category === 'closed';

  if (finished) {
    if (!open) return null;
    return (
      <Button
        loading={update.isPending}
        onClick={() =>
          update.mutate({
            body: { status: open.key },
            optimistic: { status: { ...ticket.status, key: open.key, label: open.name, category: 'open' } },
            success: `Reopened #${ticket.number}`,
          })
        }
      >
        <RotateCcw aria-hidden className="size-3.5" />
        Reopen
      </Button>
    );
  }

  if (!resolved) return null;
  return (
    <Button
      variant="primary"
      onClick={() => onResolve({ key: resolved.key, name: resolved.name, category: 'resolved' })}
    >
      <CheckCircle2 aria-hidden className="size-3.5" />
      Resolve
    </Button>
  );
}

export function ResolveDialog({
  ticket,
  target,
  onClose,
  fields,
  requireNote,
  reopenWindowDays,
}: {
  ticket: TicketDetail;
  target: ResolveTarget | null;
  onClose: () => void;
  fields: TicketFormField[];
  requireNote: boolean;
  reopenWindowDays: number;
}) {
  const update = useTicketUpdate(String(ticket.number));
  const noteId = useId();
  const [note, setNote] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  // The fields the server will insist on before this ticket can close, that
  // the ticket has not answered yet — asked for here, not discovered by a
  // refusal.
  const needed = useMemo(
    () => fields.filter((field) => field.required_to_close && isUnanswered(ticket.custom_fields?.[field.key])),
    [fields, ticket.custom_fields],
  );
  const neededKeys = useMemo(() => new Set(needed.map((field) => field.key)), [needed]);

  const close = () => {
    setNote('');
    setValues({});
    setErrors({});
    setFailure(null);
    onClose();
  };

  const submit = () => {
    const problems: Record<string, string> = {};
    if (requireNote && !note.trim()) {
      problems.resolution_note = 'Write what was done. The requester is sent this note.';
    }
    for (const field of needed) {
      if (isUnanswered(values[field.key])) problems[`custom_fields.${field.key}`] = `“${field.label}” is needed before resolving.`;
    }
    setErrors(problems);
    if (Object.keys(problems).length > 0 || !target) return;

    const answered = Object.fromEntries(Object.entries(values).filter(([, value]) => !isUnanswered(value)));
    setFailure(null);
    update.mutate(
      {
        body: {
          status: target.key,
          ...(note.trim() ? { resolution_note: note } : {}),
          ...(Object.keys(answered).length ? { custom_fields: answered } : {}),
        },
        silent: true,
        success: `${target.category === 'closed' ? 'Closed' : 'Resolved'} #${ticket.number}`,
      },
      {
        onSuccess: close,
        onError: (error) => {
          const fieldErrors = error.fieldErrors;
          if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
          setFailure(
            error.code === 'stale_write'
              ? 'Somebody else changed this ticket a moment ago. The latest version is loaded — check it and resolve again.'
              : describeError(error).title,
          );
        },
      },
    );
  };

  const verb = target?.category === 'closed' ? 'Close' : 'Resolve';

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={`${verb} #${ticket.number}`}
      description={
        target?.category === 'closed'
          ? 'Closing ends the conversation. A closing note, if you write one, is emailed to the requester as a reply.'
          : `The requester is told it is resolved, and can reply within ${reopenWindowDays} ${
              reopenWindowDays === 1 ? 'day' : 'days'
            } to reopen it.`
      }
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" loading={update.isPending} onClick={submit}>
            {verb} ticket
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {failure && (
          <p role="alert" className="rounded-control border border-critical-line bg-critical-soft px-3 py-2 text-body text-critical-ink">
            {failure}
          </p>
        )}

        {needed.length > 0 && (
          <div className="space-y-3">
            <p className="text-body text-ink-muted">
              {needed.length === 1 ? 'This field is' : 'These fields are'} needed before the ticket can close.
            </p>
            <CustomFieldInputs
              fields={needed}
              values={values}
              errors={errors}
              idPrefix="resolve-cf"
              requiredKeys={neededKeys}
              onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
            />
          </div>
        )}

        <Field
          label={target?.category === 'closed' ? 'Closing note (sent to the requester)' : 'Resolution note (sent to the requester)'}
          htmlFor={noteId}
          required={requireNote}
          error={errors.resolution_note}
          hint={requireNote ? 'Required on this desk. Say what was done.' : 'Optional. Say what was done.'}
        >
          <Textarea
            id={noteId}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                submit();
              }
            }}
            aria-invalid={errors.resolution_note ? true : undefined}
            aria-describedby={errors.resolution_note ? `${noteId}-error` : undefined}
            className="min-h-28 text-reading"
            placeholder="Replaced the docking station; the second monitor now works."
          />
        </Field>
      </div>
    </Dialog>
  );
}

export type { ResolveTarget };
